(function () {
  'use strict';

  if (!window.MWAudioPlayback) {
    let active = null;
    let sequence = 0;

    window.MWAudioPlayback = {
      claim(owner, interrupt) {
        const previous = active;
        const token = ++sequence;
        active = { owner, token, interrupt };
        if (previous && previous.owner !== owner && typeof previous.interrupt === 'function') {
          try { previous.interrupt(); } catch (_) {}
        }
        return token;
      },
      isCurrent(owner, token) {
        return Boolean(active && active.owner === owner && active.token === token);
      },
      release(owner, token) {
        if (!this.isCurrent(owner, token)) return false;
        active = null;
        return true;
      },
    };
  }

  if (window.MWAudioBufferPlayer) return;

  const playback = window.MWAudioPlayback;
  const bufferCache = new Map();
  const cacheLimit = 12;
  let sharedContext = null;

  function audioContext() {
    if (!sharedContext || sharedContext.state === 'closed') {
      const Context = window.AudioContext || window.webkitAudioContext;
      if (!Context) throw new Error('Audio playback is unavailable.');
      sharedContext = new Context({ latencyHint: 'playback' });
    }
    return sharedContext;
  }

  function trimCache() {
    let completed = [...bufferCache.values()].filter(entry => entry.complete).length;
    for (const [url, entry] of bufferCache) {
      if (completed <= cacheLimit) break;
      if (entry.complete) {
        bufferCache.delete(url);
        completed--;
      }
    }
  }

  function fetchBuffer(context, url) {
    if (typeof url !== 'string' || !url.trim()) return Promise.reject(new Error('Missing audio source.'));
    let entry = bufferCache.get(url);
    if (entry) {
      bufferCache.delete(url);
      bufferCache.set(url, entry);
      return entry.promise;
    }
    entry = { complete: false, promise: null };
    entry.promise = Promise.resolve().then(() => fetch(url))
      .then(response => {
        if (!response.ok) throw new Error('Audio request failed.');
        return response.arrayBuffer();
      })
      .then(bytes => context.decodeAudioData(bytes))
      .then(buffer => {
        if (!buffer || !(buffer.duration > 0)) throw new Error('Audio source is empty.');
        entry.complete = true;
        trimCache();
        return buffer;
      })
      .catch(error => {
        if (bufferCache.get(url) === entry) bufferCache.delete(url);
        throw error;
      });
    bufferCache.set(url, entry);
    return entry.promise;
  }

  class MWAudioBufferPlayer {
    constructor({ onChange, onError, loop = true } = {}) {
      this._onChange = onChange;
      this._onError = onError;
      this._loop = loop;
      this._track = null;
      this._state = 'on';
      this._isPlaying = false;
      this._isLoading = false;
      this._elapsed = 0;
      this._startedAt = 0;
      this._context = null;
      this._source = null;
      this._buffers = null;
      this._generation = 0;
      this._token = null;
      this._playPromise = null;
      this._destroyed = false;
    }

    get track() { return this._track; }
    get state() { return this._state; }
    get isPlaying() { return this._isPlaying; }
    get isLoading() { return this._isLoading; }
    get duration() { return this._buffer()?.duration || 0; }
    get currentTime() {
      const elapsed = this._isPlaying && this._context
        ? this._elapsed + this._context.currentTime - this._startedAt
        : this._elapsed;
      return this._position(elapsed);
    }

    _buffer() {
      return this._buffers && (this._buffers.single || this._buffers[this._state]);
    }

    _position(seconds) {
      const time = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
      const duration = this.duration;
      return duration ? (this._loop ? time % duration : Math.min(time, duration)) : time;
    }

    _notify() {
      if (typeof this._onChange !== 'function') return;
      const snapshot = {
        track: this.track,
        state: this.state,
        isPlaying: this.isPlaying,
        isLoading: this.isLoading,
        currentTime: this.currentTime,
        duration: this.duration,
      };
      try { this._onChange(snapshot); } catch (_) {}
    }

    _stopSource() {
      const source = this._source;
      this._source = null;
      if (!source) return;
      source.onended = null;
      try { source.stop(); } catch (_) {}
      try { source.disconnect(); } catch (_) {}
    }

    _halt(reset) {
      this._elapsed = reset ? 0 : this.currentTime;
      this._generation++;
      this._stopSource();
      this._isPlaying = false;
      this._isLoading = false;
      this._playPromise = null;
      playback.release(this, this._token);
      this._token = null;
    }

    setTrack(track) {
      if (this._destroyed) return;
      this._halt(true);
      this._track = track || null;
      this._buffers = null;
      this._notify();
    }

    _valid(generation, token) {
      return !this._destroyed && this._generation === generation && playback.isCurrent(this, token);
    }

    _startSource() {
      if (!playback.isCurrent(this, this._token)) return false;
      const buffer = this._buffer();
      if (!buffer) return false;
      const offset = this._position(this._elapsed);
      if (!this._loop && offset >= buffer.duration) {
        this.pause({ reset: true });
        return false;
      }
      const source = this._context.createBufferSource();
      source.buffer = buffer;
      source.loop = this._loop;
      source.connect(this._context.destination);
      this._source = source;
      source.onended = () => {
        if (this._source !== source) return;
        this._halt(true);
        this._notify();
      };
      source.start(0, offset);
      this._elapsed = offset;
      this._startedAt = this._context.currentTime;
      this._isPlaying = true;
      return true;
    }

    _fail(error) {
      this._halt(true);
      this._notify();
      if (typeof this._onError === 'function') {
        try { this._onError(error); } catch (_) {}
      }
    }

    play() {
      if (this._destroyed || !this._track) return Promise.resolve(false);
      if (this._isPlaying) return Promise.resolve(true);
      if (this._isLoading && this._playPromise) return this._playPromise;

      const generation = ++this._generation;
      const token = playback.claim(this, () => this.pause());
      this._token = token;
      if (!this._valid(generation, token)) {
        playback.release(this, token);
        this._token = null;
        return Promise.resolve(false);
      }
      this._isLoading = true;

      let resumed;
      let loaded;
      try {
        this._context = audioContext();
        resumed = this._context.state === 'running' ? Promise.resolve() : this._context.resume();
        const track = this._track;
        loaded = this._buffers ? Promise.resolve(this._buffers) : track.on || track.off
          ? Promise.all([fetchBuffer(this._context, track.on), fetchBuffer(this._context, track.off)])
            .then(([on, off]) => ({ on, off }))
          : fetchBuffer(this._context, track.url).then(single => ({ single }));
      } catch (error) {
        this._fail(error);
        return Promise.resolve(false);
      }

      const pending = Promise.all([resumed, loaded])
        .then(([, buffers]) => {
          if (!this._valid(generation, token)) return false;
          this._buffers = buffers;
          this._isLoading = false;
          const started = this._startSource();
          this._notify();
          return started;
        })
        .catch(error => {
          if (this._valid(generation, token)) this._fail(error);
          return false;
        })
        .finally(() => {
          if (this._playPromise === pending) this._playPromise = null;
        });
      this._playPromise = pending;
      this._notify();
      return pending;
    }

    pause({ reset = false } = {}) {
      if (this._destroyed) return;
      this._halt(reset);
      this._notify();
    }

    toggle() {
      if (this.isPlaying || this.isLoading) {
        this.pause();
        return Promise.resolve(false);
      }
      return this.play();
    }

    setState(state) {
      if (this._destroyed || !['on', 'off'].includes(state) || state === this._state) return;
      const elapsed = this.currentTime;
      const wasPlaying = this._isPlaying;
      this._stopSource();
      this._isPlaying = false;
      this._state = state;
      this._elapsed = elapsed;
      try {
        if (wasPlaying) this._startSource();
        this._notify();
      } catch (error) { this._fail(error); }
    }

    seek(seconds) {
      if (this._destroyed || !Number.isFinite(seconds)) return;
      const wasPlaying = this._isPlaying;
      this._stopSource();
      this._isPlaying = false;
      this._elapsed = this._position(seconds);
      try {
        if (wasPlaying) this._startSource();
        this._notify();
      } catch (error) { this._fail(error); }
    }

    destroy() {
      if (this._destroyed) return;
      this._halt(true);
      this._track = null;
      this._buffers = null;
      this._destroyed = true;
      this._notify();
      this._onChange = null;
      this._onError = null;
    }
  }

  window.MWAudioBufferPlayer = MWAudioBufferPlayer;
})();
